"use client";

// CANONICAL SOURCE — flare/reporters/next/components/FlareClient.tsx
// Copied to each Next app as `components/FlareClient.tsx` and mounted ONCE in the
// root layout, inside <body>.

import { useEffect } from "react";
import { readIdentity, redactTokenPath } from "../lib/flare";

const MAX_BUFFERED = 20;
const FLUSH_MS = 5_000;

type Buffered = {
  kind: "client";
  name: string;
  message: string;
  stack: string | null;
  url: string | null;
  release: string | null;
  environment: "production";
  occurredAt: number;
  context: null;
  userRef: string | null;
  sessionId: string | null;
};

// Session cookies (no Max-Age/Expires): they die with the browser session, and
// they exist only so the server half (instrumentation.ts) can read the same ids.
const COOKIE_ATTRS = "Path=/; SameSite=Lax; Secure";
const setCookie = (name: string, value: string) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; ${COOKIE_ATTRS}`;
};

/**
 * `identity="next-auth"` opts an Auth.js app into per-person tracing: once per
 * tab, after the page goes idle, this asks `/api/auth/session` for the signed-in
 * user's id and keeps it as `flare_uid`. Without the prop only the per-tab
 * session id is sent. The id, never the email: ingest drops any value with `@`.
 */
// Module state lives exactly as long as the page load: a client-side navigation
// or a remount keeps it, a reload resets it.
let userLookupThisLoad = false;

export default function FlareClient({ identity }: { identity?: "next-auth" }) {
  useEffect(() => {
    const endpoint = process.env.NEXT_PUBLIC_FLARE_URL;
    const key = process.env.NEXT_PUBLIC_FLARE_KEY;
    if (!endpoint || !key || process.env.NEXT_PUBLIC_VERCEL_ENV !== "production") return;

    const release = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? null;
    let buffer: Buffered[] = [];
    let dropped = 0;

    // ONE SESSION ID PER TAB. sessionStorage is per tab and survives reloads, so
    // the id ties together every error in one sitting. Blocked storage (Safari
    // private mode throws here) means no id, never a thrown reporter.
    try {
      if (!sessionStorage.getItem("flare_sid")) sessionStorage.setItem("flare_sid", crypto.randomUUID());
    } catch {
      // Report without a session id.
    }

    // THE COOKIE IS SHARED BY EVERY TAB; sessionStorage is not. Whichever tab
    // wrote it last wins, so each tab re-writes its own ids whenever it becomes
    // the one in front: the server errors a person can trigger come from the tab
    // they are using. Written only from what storage holds, so the two halves
    // never disagree.
    const mirrorToCookies = () => {
      try {
        const sid = sessionStorage.getItem("flare_sid");
        if (sid) setCookie("flare_sid", sid);
        const uid = sessionStorage.getItem("flare_uid");
        if (uid) setCookie("flare_uid", uid);
      } catch {
        // Blocked storage or cookies: nothing to mirror.
      }
    };
    mirrorToCookies();

    // AT MOST ONE LOOKUP PER PAGE LOAD, and one per TAB when the endpoint is
    // plainly not Auth.js (a 404, a body that is not JSON, or JSON that is not
    // an object or null): only that verdict is kept in sessionStorage.
    // A signed-out reply is remembered in memory only, so a sign-in by OAuth
    // redirect, which reloads the page in the same tab, is still learned. Auth.js
    // v5 answers signed-out with the JSON body `null` (@auth/core's session
    // action), older versions with `{}`; both are "signed out", never "not
    // Auth.js". A 5xx, any other non-2xx, or a network failure is transient and
    // forgotten with the page load. A found id is kept for the tab.
    let idleHandle: number | null = null;
    let idleIsTimeout = false;
    const lookUpUser = async () => {
      idleHandle = null;
      userLookupThisLoad = true;
      let notAuthJs = false;
      try {
        const res = await fetch("/api/auth/session", { credentials: "same-origin" });
        if (res.status === 404) {
          notAuthJs = true;
        } else if (res.ok) {
          let json: unknown;
          try {
            json = await res.json();
          } catch {
            notAuthJs = true;
          }
          if (!notAuthJs && json !== null) {
            if (typeof json !== "object" || Array.isArray(json)) {
              notAuthJs = true;
            } else {
              const id = (json as { user?: { id?: unknown } }).user?.id;
              const ref = typeof id === "string" && id ? id : typeof id === "number" && Number.isFinite(id) ? String(id) : null;
              if (ref) {
                sessionStorage.setItem("flare_uid", ref);
                setCookie("flare_uid", ref);
                return;
              }
            }
          }
        }
      } catch {
        // A network failure is transient: forgotten with the page load.
      }
      try {
        if (notAuthJs) sessionStorage.setItem("flare_uid_checked", "1");
        // Signed out (or someone else's cookie left over in this browser).
        document.cookie = `flare_uid=; ${COOKIE_ATTRS}; Max-Age=0`;
      } catch {
        // Nothing to record or clear.
      }
    };
    if (identity === "next-auth" && !userLookupThisLoad) {
      try {
        // Reading storage first also proves it usable: if it cannot be read, a
        // found id could not be kept either, so no lookup is made at all.
        if (!sessionStorage.getItem("flare_uid") && !sessionStorage.getItem("flare_uid_checked")) {
          if (typeof window.requestIdleCallback === "function") {
            idleHandle = window.requestIdleCallback(() => void lookUpUser(), { timeout: 5_000 });
          } else {
            idleIsTimeout = true;
            idleHandle = window.setTimeout(() => void lookUpUser(), 2_000);
          }
        }
      } catch {
        // Blocked storage: no lookup.
      }
    }

    const push = (name: string, message: string, stack: string | null) => {
      // A render loop can throw thousands of times per second. Past the cap we
      // stop buffering but keep counting, so the flush can say how many were lost
      // rather than silently under-reporting.
      if (buffer.length >= MAX_BUFFERED) {
        dropped += 1;
        return;
      }
      buffer.push({
        kind: "client",
        name: name || "Error",
        message: message || "Unknown error",
        stack,
        // No query string: the server half sends request.path with no query
        // too, so the two halves agree, and a query string can carry a real
        // identifier in some apps (vitals, tide) — not part of the
        // fingerprint, so dropping it costs nothing but exposure. A caller
        // that needs a query value passes it explicitly via context. A bearer
        // token in the path (/invite/<token>) is a credential: redacted.
        url: redactTokenPath(window.location.pathname),
        release,
        environment: "production",
        occurredAt: Date.now(),
        context: null,
        // Read at push time: the user lookup may land after the page loads.
        ...readIdentity(),
      });
    };

    const flush = (useBeacon: boolean) => {
      if (buffer.length === 0) return;
      const events = buffer;
      const lost = dropped;
      buffer = [];
      dropped = 0;
      if (lost > 0) {
        events.push({
          kind: "client",
          name: "FlareBufferOverflow",
          message: `${lost} further client errors on this page load were not buffered`,
          stack: null,
          url: redactTokenPath(window.location.pathname),
          release,
          environment: "production",
          occurredAt: Date.now(),
          context: null,
          ...readIdentity(),
        });
      }
      const body = JSON.stringify({ events });
      try {
        // sendBeacon survives the page being torn down; fetch does not. It cannot
        // set a custom header, so the key rides as a query parameter on the
        // beacon path only. It is an ingest key, not a user credential.
        if (useBeacon && typeof navigator.sendBeacon === "function") {
          navigator.sendBeacon(`${endpoint}?k=${encodeURIComponent(key)}`, new Blob([body], { type: "application/json" }));
          return;
        }
        void fetch(endpoint, {
          method: "POST",
          headers: { "content-type": "application/json", "x-flare-key": key },
          body,
          signal: AbortSignal.timeout(2_000),
          keepalive: true,
        }).catch(() => {});
      } catch {
        // Never let reporting break the page.
      }
    };

    // `window.onerror` still names the file, line and column when `e.error` is
    // null — a throw the browser could not hand us an Error for. Synthesising a
    // single V8-shaped frame from them is the difference between a group with a
    // real culprit and an anonymous "Error" with nothing to act on; flare's
    // culpritFrames parses this exact shape.
    //
    // The one throw that gives neither an Error nor a filename is a CROSS-ORIGIN
    // one: the browser replaces the message with the literal "Script error." and
    // blanks filename/lineno/colno, deliberately, so a page cannot read another
    // origin's internals. That combination is unfixable by anyone reading it, and
    // flare's ingest drops it on exactly that signature — which is only safe
    // BECAUSE this handler now populates a frame in every case that is not it.
    const frameFrom = (e: ErrorEvent) =>
      e.filename ? `    at ${e.filename}:${e.lineno ?? 0}:${e.colno ?? 0}` : null;
    const onError = (e: ErrorEvent) =>
      push(e.error?.name ?? "Error", e.message, e.error?.stack ?? frameFrom(e));
    const onRejection = (e: PromiseRejectionEvent) => {
      const r = e.reason;
      push(r?.name ?? "UnhandledRejection", r?.message ?? String(r), r?.stack ?? null);
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush(true);
      else mirrorToCookies();
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", mirrorToCookies);
    const timer = window.setInterval(() => flush(false), FLUSH_MS);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", mirrorToCookies);
      window.clearInterval(timer);
      if (idleHandle !== null) {
        if (idleIsTimeout) window.clearTimeout(idleHandle);
        else window.cancelIdleCallback?.(idleHandle);
      }
      flush(true);
    };
  }, [identity]);

  return null;
}
